import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDay, parseIsoDay } from '@wodi/domain';
import type { RosterUnitView, RosterView } from '@wodi/shared';
import { Button, Checkbox, Dialog, DialogActions, Notice, Select } from '@wodi/ui';
import { useState } from 'react';

import {
  addRosterEntries,
  candidatesQuery,
  removeRosterEntry,
  requestSwap,
  rosterKeys,
} from '../api/roster.ts';
import { errorText } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { LicenceWarning } from './words.tsx';

// Dialogs on the roster (docs/plans/phase3-operations-modules.md §4.3): add people to the
// roster, remove an entry, ask to swap one's own shift.

export function dayLabel(iso: string): string {
  const parsed = parseIsoDay(iso);
  return parsed.ok ? formatDay(parsed.value) : iso;
}

export function AddToRosterDialog({
  unit,
  roster,
  days,
  onClose,
}: {
  unit: RosterUnitView;
  roster: RosterView;
  days: string[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const candidates = useQuery(candidatesQuery(unit.type === 'department' ? unit.key : ''));
  const [person, setPerson] = useState<string | null>(null);
  const [shift, setShift] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const add = useMutation({
    mutationFn: () =>
      addRosterEntries({
        unitType: unit.type,
        unit: unit.key,
        userId: person ?? '',
        shiftId: shift ?? '',
        days: chosen,
      }),
    onSuccess: ({ added }) => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.all });
      toasts.show({
        title: `Added to the roster, ${String(added)} ${added === 1 ? 'day' : 'days'}`,
      });
      onClose();
    },
  });
  const picked = candidates.data?.items.find((p) => p.userId === person);
  return (
    <Dialog
      title="Add to roster"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <div className={styles.form}>
        <Select
          label="Person"
          placeholder={candidates.isPending ? 'Loading…' : 'Choose who'}
          {...(unit.type === 'department' ? { description: `People in ${unit.name} first.` } : {})}
          options={(candidates.data?.items ?? []).map((p) => ({
            id: p.userId,
            label: p.department === null ? p.displayName : `${p.displayName}, ${p.department}`,
          }))}
          value={person}
          onChange={(key) => {
            setPerson(key === null ? null : String(key));
          }}
        />
        {picked === undefined ? null : <LicenceWarning licence={picked.licence} />}
        <Select
          label="Shift"
          placeholder="Choose the shift"
          options={roster.shifts
            .filter((s) => s.active)
            .map((s) => ({ id: s.id, label: `${s.name}, ${s.startsAt} to ${s.endsAt}` }))}
          value={shift}
          onChange={(key) => {
            setShift(key === null ? null : String(key));
          }}
        />
        <fieldset className={styles.form}>
          <legend>Days</legend>
          {days.map((d) => (
            <Checkbox
              key={d}
              isSelected={chosen.includes(d)}
              onChange={(on) => {
                setChosen(on ? [...chosen, d] : chosen.filter((x) => x !== d));
              }}
            >
              {dayLabel(d)}
            </Checkbox>
          ))}
        </fieldset>
        {add.isError ? <Notice tone="error">{errorText(add.error, 'Try again.')}</Notice> : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isDisabled={person === null || shift === null || chosen.length === 0}
          isPending={add.isPending}
          pendingLabel="Adding…"
          onPress={() => {
            add.mutate();
          }}
        >
          Add to roster
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface EntryChoice {
  id: string;
  userId: string;
  onDate: string;
  shiftName: string;
  personName: string;
}

export function RemoveEntryDialog({ entry, onClose }: { entry: EntryChoice; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: (reason: string) => removeRosterEntry(entry.id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.all });
      toasts.show({ title: 'Removed from the roster' });
      onClose();
    },
  });
  return (
    <ReasonDialog
      title="Remove from the roster"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      confirmLabel="Remove from roster"
      pendingLabel="Removing…"
      isPending={remove.isPending}
      error={remove.isError ? errorText(remove.error, 'Try again.') : null}
      onConfirm={(reason) => {
        remove.mutate(reason);
      }}
    >
      <p className={styles.prose}>
        {entry.personName}, {entry.shiftName} on {dayLabel(entry.onDate)}.
      </p>
    </ReasonDialog>
  );
}

const HAND_OVER = 'none';

export function SwapDialog({
  entry,
  roster,
  onClose,
}: {
  entry: EntryChoice;
  roster: RosterView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [colleague, setColleague] = useState<string | null>(null);
  const [theirs, setTheirs] = useState(HAND_OVER);
  const shiftName = (id: string) => roster.shifts.find((s) => s.id === id)?.name ?? '';
  const ask = useMutation({
    mutationFn: () =>
      requestSwap({
        fromEntryId: entry.id,
        toUserId: colleague ?? '',
        toEntryId: theirs === HAND_OVER ? null : theirs,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.swaps });
      toasts.show({ title: 'Swap requested' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Ask to swap"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <div className={styles.form}>
        <p className={styles.prose}>
          Your {entry.shiftName} shift on {dayLabel(entry.onDate)}. Someone who manages the roster
          approves it.
        </p>
        <Select
          label="Colleague"
          placeholder="Choose who"
          options={roster.people
            .filter((p) => p.userId !== entry.userId)
            .map((p) => ({ id: p.userId, label: p.displayName }))}
          value={colleague}
          onChange={(key) => {
            setColleague(key === null ? null : String(key));
            setTheirs(HAND_OVER);
          }}
        />
        <Select
          label="In return"
          isDisabled={colleague === null}
          options={[
            { id: HAND_OVER, label: 'Nothing: they take my shift' },
            ...roster.entries
              .filter((e) => e.userId === colleague)
              .map((e) => ({
                id: e.id,
                label: `Their ${shiftName(e.shiftId)} on ${dayLabel(e.onDate)}`,
              })),
          ]}
          value={theirs}
          onChange={(key) => {
            setTheirs(key === null ? HAND_OVER : String(key));
          }}
        />
        {ask.isError ? <Notice tone="error">{errorText(ask.error, 'Try again.')}</Notice> : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isDisabled={colleague === null}
          isPending={ask.isPending}
          pendingLabel="Asking…"
          onPress={() => {
            ask.mutate();
          }}
        >
          Ask to swap
        </Button>
      </DialogActions>
    </Dialog>
  );
}
